import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import { currentYearRange } from "../utils/payrollEftReportUtils";
import {
  DEFAULT_COMPANY,
  buildCompanyPayerBlock,
  computeNetCommissionForAgent,
  formatT4aAmount,
} from "../utils/t4aPdfFill";
import {
  T4A_SUMMARY_FILLABLE_DEFAULTS,
  base64ToUint8,
  fillT4aSummaryPdfBytes,
  loadT4aSummaryTemplateBytes,
  uint8ToBase64,
} from "../utils/t4aSummaryPdfFill";

function normalizeCompany(companyRaw) {
  const raw = companyRaw && typeof companyRaw === "object" ? companyRaw : {};
  return {
    companyName:
      String(raw.companyName || "").trim() || DEFAULT_COMPANY.companyName,
    address: String(raw.address || "").trim() || DEFAULT_COMPANY.address,
    city: String(raw.city || "").trim() || DEFAULT_COMPANY.city,
    province: String(raw.province || "").trim() || DEFAULT_COMPANY.province,
    postalCode:
      String(raw.postalCode || "").trim() || DEFAULT_COMPANY.postalCode,
    hstNumber: String(raw.hstNumber || "").trim(),
  };
}

function toPdfBlob(bytes) {
  const copy =
    bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  const ab = copy.buffer.slice(
    copy.byteOffset,
    copy.byteOffset + copy.byteLength,
  );
  return new Blob([ab], { type: "application/pdf" });
}

const FILLABLE_FIELDS = [
  { key: "remittances", label: "Minus: remittances (082)" },
  { key: "difference", label: "Difference" },
  { key: "overpayment", label: "Overpayment (084)" },
  { key: "balanceDue", label: "Balance due (086)" },
  { key: "amountEnclosed", label: "Amount enclosed" },
  { key: "line101", label: "Box 101" },
  { key: "registration1", label: "Registration # (071)", maxLength: 7 },
  { key: "registration2", label: "Registration # (072)", maxLength: 7 },
  { key: "registration3", label: "Registration # (073)", maxLength: 7 },
  { key: "preparedBySin1", label: "SIN of person who prepared (074)", maxLength: 9 },
  { key: "preparedBySin2", label: "SIN of person who prepared (075)", maxLength: 9 },
  { key: "certName", label: "Name of authorized person (076)" },
  { key: "areaCode", label: "Areacode (078)", maxLength: 3 },
  { key: "certPhone", label: "Telephone" },
  { key: "certExt", label: "Extension" },
  { key: "certDate", label: "Certification date" },
  { key: "certPosition", label: "Position or office" },
];

export default function T4ASummaryPage() {
  const { appUser, isAdmin, isSuperUser } = useRole();
  const isOfficeUser = isAdmin || isSuperUser;
  const yearRange = useMemo(() => currentYearRange(), []);
  const iframeRef = useRef(null);

  const [bootLoading, setBootLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [company, setCompany] = useState(DEFAULT_COMPANY);
  const [payerAccount, setPayerAccount] = useState("");
  const [slipCount, setSlipCount] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [fillable, setFillable] = useState(T4A_SUMMARY_FILLABLE_DEFAULTS);
  const [posted, setPosted] = useState(false);
  const [postedMeta, setPostedMeta] = useState(null);

  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfBytes, setPdfBytes] = useState(null);

  const clearPdf = useCallback(() => {
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setPdfBytes(null);
  }, []);

  const showSavedPdf = useCallback((bytes) => {
    const blob = toPdfBlob(bytes);
    const url = URL.createObjectURL(blob);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setPdfBytes(
      bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []),
    );
  }, []);

  const buildPdf = useCallback(
    async (overrides = {}) => {
      setBuilding(true);
      setError("");
      try {
        const template = await loadT4aSummaryTemplateBytes();
        const companySource = overrides.company ?? company;
        const accountSource = overrides.payerAccount ?? payerAccount;
        const slips = overrides.slipCount ?? slipCount;
        const total = overrides.totalPayment ?? totalPayment;
        const fill = overrides.fillable ?? fillable;

        const filled = await fillT4aSummaryPdfBytes(template, {
          year: yearRange.year,
          companyBlock: buildCompanyPayerBlock(companySource),
          payerAccount: accountSource,
          slipCount: slips,
          totalPayment: total,
          fillable: fill,
        });
        showSavedPdf(filled);
      } catch (err) {
        clearPdf();
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to build T4A Summary PDF.",
        );
      } finally {
        setBuilding(false);
      }
    },
    [
      clearPdf,
      company,
      fillable,
      payerAccount,
      showSavedPdf,
      slipCount,
      totalPayment,
      yearRange.year,
    ],
  );

  useEffect(() => {
    if (!isOfficeUser) {
      setBootLoading(false);
      setError("T4A Summary is only available to office admins.");
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setBootLoading(true);
      setError("");
      setInfo("");
      try {
        const [postsRes, summaryRes, eftRes, agentsRes, companyRes, miscRes] =
          await Promise.all([
            axiosInstance.get("/t4a-posts", {
              params: { year: yearRange.year },
            }),
            axiosInstance
              .get("/t4a-summary-posts", { params: { year: yearRange.year } })
              .catch(() => ({ data: { posted: false } })),
            axiosInstance.get("/commission-trust-eft"),
            axiosInstance.get("/agents"),
            axiosInstance.get("/company-profile").catch(() => ({ data: null })),
            axiosInstance.get("/misc-settings").catch(() => ({ data: null })),
          ]);
        if (cancelled) return;

        const posts = Array.isArray(postsRes.data) ? postsRes.data : [];
        const agentList = Array.isArray(agentsRes.data) ? agentsRes.data : [];
        const eftList = Array.isArray(eftRes.data) ? eftRes.data : [];
        const misc = Array.isArray(miscRes.data)
          ? miscRes.data[0]
          : miscRes.data;
        const companyRaw = Array.isArray(companyRes.data)
          ? companyRes.data[0]
          : companyRes.data;
        const companyNorm = normalizeCompany(companyRaw);
        const account =
          String(companyNorm.hstNumber || "").trim() ||
          (misc?.hstNumber != null && misc?.hstNumber !== ""
            ? String(misc.hstNumber)
            : "");

        const postedAgentIds = posts.map((p) => String(p.agentId));
        let total = 0;
        for (const id of postedAgentIds) {
          const agent = agentList.find((a) => String(a._id) === id);
          if (!agent) continue;
          total += computeNetCommissionForAgent(
            eftList,
            agentList,
            agent,
            yearRange,
          );
        }
        total = Math.round(total * 100) / 100;
        const slips = posts.length;

        setCompany(companyNorm);
        setPayerAccount(account);
        setSlipCount(slips);
        setTotalPayment(total);

        const summary = summaryRes?.data;
        if (summary?.posted && summary?.post?.pdfBase64) {
          const savedFill = {
            ...T4A_SUMMARY_FILLABLE_DEFAULTS,
            ...(summary.post.fillable || {}),
          };
          setFillable(savedFill);
          setPosted(true);
          setPostedMeta({
            postedAt: summary.post.postedAt,
            postedBy: summary.post.postedBy,
            slipCount: summary.post.slipCount,
            totalPayment: summary.post.totalPayment,
          });
          showSavedPdf(base64ToUint8(summary.post.pdfBase64));
          setInfo(
            `Posted T4A Summary for ${yearRange.year} is locked. You can view, download, or print it (office only — not sent to agents).`,
          );
        } else {
          setPosted(false);
          setPostedMeta(null);
          setFillable(T4A_SUMMARY_FILLABLE_DEFAULTS);
          setInfo(
            slips === 0
              ? "No T4A slips posted yet for this year. Box 088 / Box 020 will update once slips are posted on the T4A page."
              : `${slips} posted T4A slip${slips === 1 ? "" : "s"}. Fill optional blanks, then Post. Once posted, it cannot be edited.`,
          );
          await buildPdf({
            company: companyNorm,
            payerAccount: account,
            slipCount: slips,
            totalPayment: total,
            fillable: T4A_SUMMARY_FILLABLE_DEFAULTS,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load T4A Summary.",
          );
          clearPdf();
        }
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfficeUser, yearRange.year]);

  // Rebuild preview when fillable fields change (draft only — before post).
  useEffect(() => {
    if (bootLoading || posted || !isOfficeUser) return undefined;
    const t = setTimeout(() => {
      buildPdf();
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillable]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const onFillableChange = (key, value) => {
    if (posted) return;
    setFillable((prev) => ({ ...prev, [key]: value }));
  };

  const handlePost = async () => {
    if (!isOfficeUser || posted) return;
    setPosting(true);
    setError("");
    try {
      const template = await loadT4aSummaryTemplateBytes();
      const filled = await fillT4aSummaryPdfBytes(template, {
        year: yearRange.year,
        companyBlock: buildCompanyPayerBlock(company),
        payerAccount,
        slipCount,
        totalPayment,
        fillable,
      });
      showSavedPdf(filled);

      const postedBy = appUser?.email || appUser?.displayName || "";
      const res = await axiosInstance.post("/t4a-summary-posts", {
        year: yearRange.year,
        slipCount,
        totalPayment,
        fillable,
        pdfBase64: uint8ToBase64(filled),
        postedBy,
      });
      setPosted(true);
      setPostedMeta({
        postedAt: res.data?.postedAt || new Date().toISOString(),
        postedBy: res.data?.postedBy || postedBy,
        slipCount,
        totalPayment,
      });
      setInfo(
        "T4A Summary posted and locked. You can view, download, or print it (office only — not sent to agents).",
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to post T4A Summary.",
      );
    } finally {
      setPosting(false);
    }
  };

  const handleDownload = async () => {
    setError("");
    try {
      if (posted) {
        const res = await axiosInstance.get(
          `/t4a-summary-posts/${yearRange.year}/pdf`,
          { responseType: "blob" },
        );
        const blob =
          res.data instanceof Blob
            ? res.data
            : new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `T4A-Summary-${yearRange.year}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      }

      if (!pdfBytes?.length) {
        setError("No PDF available to download.");
        return;
      }
      const blob = toPdfBlob(pdfBytes);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `T4A-Summary-${yearRange.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (pdfBytes?.length) {
        const blob = toPdfBlob(pdfBytes);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `T4A-Summary-${yearRange.year}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      }
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to download T4A Summary.",
      );
    }
  };

  const handlePrint = () => {
    setError("");
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      try {
        iframe.focus();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        return;
      } catch (_) {
        // Fall through to new-tab print.
      }
    }
    if (!pdfUrl) {
      setError("No PDF available to print.");
      return;
    }
    const w = window.open(pdfUrl, "_blank");
    if (!w) {
      setError("Pop-up blocked. Allow pop-ups to print, or use Download.");
      return;
    }
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (_) {}
    }, 800);
  };

  const canUsePdf = Boolean(pdfUrl && pdfBytes?.length);
  const displaySlipCount = postedMeta?.slipCount ?? slipCount;
  const displayTotal = postedMeta?.totalPayment ?? totalPayment;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
              T4A Summary — {yearRange.year}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Period: {yearRange.label}
              {posted ? " · Posted (locked)" : ""}
            </p>
            {!bootLoading ? (
              <p className="mt-1 text-sm text-slate-600">
                Box 088 (slips):{" "}
                <span className="font-medium tabular-nums">
                  {displaySlipCount}
                </span>
                {" · "}
                Box 020 (Total Payment):{" "}
                <span className="font-medium tabular-nums">
                  ${formatT4aAmount(displayTotal)}
                </span>
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isOfficeUser && !posted ? (
              <button
                type="button"
                onClick={handlePost}
                disabled={posting || building || bootLoading || !canUsePdf}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {posting ? "Posting…" : "Post"}
              </button>
            ) : null}
            {posted ? (
              <span className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-800 border border-emerald-200">
                Posted
              </span>
            ) : null}
            <button
              type="button"
              onClick={handleDownload}
              disabled={!canUsePdf || bootLoading}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 disabled:opacity-50"
            >
              Download PDF
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!canUsePdf || bootLoading}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-900 text-white hover:bg-blue-800 disabled:opacity-50"
            >
              Print
            </button>
          </div>
        </div>

        {error ? (
          <div
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {info && !error ? (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-700">
            {info}
          </div>
        ) : null}

        {bootLoading ? (
          <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
            Loading…
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-start">
            {isOfficeUser && !posted ? (
              <aside className="w-full lg:w-72 xl:w-80 flex-shrink-0 rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col max-h-[70vh] lg:max-h-[calc(100vh-12rem)]">
                <div className="px-3 py-3 border-b border-slate-200 flex-shrink-0">
                  <h2 className="text-sm font-semibold text-slate-800">
                    Fillable fields
                  </h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Auto-filled: year, HST #, company, Box 088, Box 020, and
                    0.00 boxes. Edit the blanks below, then Post. After posting,
                    the form is locked.
                  </p>
                </div>
                <div className="overflow-y-auto p-3 space-y-3">
                  {FILLABLE_FIELDS.map((field) => (
                    <label key={field.key} className="block">
                      <span className="block text-xs font-medium text-slate-600 mb-1">
                        {field.label}
                      </span>
                      <input
                        type="text"
                        value={fillable[field.key] || ""}
                        maxLength={field.maxLength || undefined}
                        onChange={(e) =>
                          onFillableChange(field.key, e.target.value)
                        }
                        className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                      />
                    </label>
                  ))}
                </div>
              </aside>
            ) : null}

            {isOfficeUser && posted ? (
              <aside className="w-full lg:w-64 flex-shrink-0 rounded-xl border border-slate-200 bg-white shadow-sm p-4">
                <h2 className="text-sm font-semibold text-slate-800">
                  Posted summary
                </h2>
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                  This form is locked and cannot be edited. You can view,
                  download, or print it.
                </p>
                {postedMeta?.postedAt ? (
                  <p className="mt-3 text-xs text-slate-600">
                    Posted{" "}
                    {new Date(postedMeta.postedAt).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                    {postedMeta.postedBy ? ` · ${postedMeta.postedBy}` : ""}
                  </p>
                ) : null}
              </aside>
            ) : null}

            <div className="min-w-0 flex-1">
              {building && !pdfUrl ? (
                <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
                  Preparing T4A Summary…
                </div>
              ) : pdfUrl ? (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden relative">
                  {building ? (
                    <div className="absolute inset-x-0 top-0 z-10 bg-slate-900/70 text-white text-xs text-center py-1">
                      Updating preview…
                    </div>
                  ) : null}
                  <iframe
                    ref={iframeRef}
                    title="T4A Summary PDF"
                    src={pdfUrl}
                    className="w-full min-h-[80vh] border-0"
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
                  No T4A Summary to display.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
